import IOwTableCols from "../../models/IOwTableCols";
import IPackingListViewOptions from "../../models/IPackingListViewOptions";
import IOrder_ItemUI from "../../models/IOrder_Item";
import OwS3ImageViewer from "../../components/shared/OwS3ImageViewer/OwS3ImageViewer";
import { OwBarcode } from "../../components/shared/OwBarcode/OwBarcode";
import React, { ChangeEvent, useMemo } from "react";
import IPacking_List from "../../models/IPacking_List";
import ISupplierModel from "../../models/ISupplierModel";
import OwInputWrapper from "../../components/shared/OwInputWrapper/OwInputWrapper";
import OwCheckbox from "../../components/shared/OwCheckbox/OwCheckbox";
import IQtyAssigned from "../../models/IQtyAssigned";

/**
 * This hook is responsible for setting up the main row columns for the packing list view.
 *
 * @param viewOptions
 * @param packingList
 * @param supplierModel
 * @param qtyAssigned
 * @param handleQtyAssignedChange
 * @param hasLotsChange
 * @param handleCartonChange
 * @param hasAlternateSources
 */
export const usePackingListViewColumns = (
    viewOptions: IPackingListViewOptions,
    packingList: IPacking_List,
    supplierModel: ISupplierModel,
    qtyAssigned: IQtyAssigned,
    handleQtyAssignedChange: (e: any, row: IOrder_ItemUI) => void,
    hasLotsChange: (e: ChangeEvent<any>, row: IOrder_ItemUI) => void,
    handleCartonChange: (e: any, row: IOrder_ItemUI) => void,
    hasAlternateSources: boolean
): IOwTableCols[] => {
    const columns = useMemo(() => {
        const showChevronFn = (row: IOrder_ItemUI): boolean => {
            return !!row.Catalog_Item?.has_alternate_sourcing;
        };

        const orderRenderFn = (row: IOrder_ItemUI) => {
            if (packingList.Orders) {
                for (const order of packingList.Orders) {
                    if (row.order_id === order.id) {
                        return order.name;
                    }
                }
            } else {
                return "";
            }
        };

        const renderSkuFn = (row: IOrder_ItemUI) => {
            if (row.Catalog_Item?.sku) {
                return (
                    <div className={"d-flex " + (viewOptions.showItemPhoto ? "" : "flex-column")}>
                        <div>
                            {viewOptions.showItemPhoto ? (
                                <div>
                                    <OwS3ImageViewer catalogItem={row.Catalog_Item} />
                                </div>
                            ) : null}
                            <div>{row.Catalog_Item?.sku || ""}</div>
                        </div>

                        {viewOptions.hideSkuCol ? (
                            <OwBarcode format={"CODE128"} value={row.Catalog_Item?.sku || ""} height={40} displayValue={false} />
                        ) : null}
                    </div>
                );
            } else {
                return null;
            }
        };

        const renderUpcFn = (row: IOrder_ItemUI) => {
            if (row.Catalog_Item?.upc) {
                return <OwBarcode format={"UPC"} value={row.Catalog_Item.upc} height={25} displayValue={true} />;
            } else {
                return null;
            }
        };

        const renderUomFn = (row: IOrder_ItemUI) => {
            if (row.Catalog_Item?.Unit_Of_Measurement?.abbreviation) {
                return row.Catalog_Item?.Unit_Of_Measurement?.abbreviation;
            } else {
                return null;
            }
        };

        const pickQtyRenderFn = (row: IOrder_ItemUI) => {
            return qtyAssigned[row.guid].qty_picked;
        };

        const orderQtyRenderFn = (row: IOrder_ItemUI) => {
            return row.qty;
        };

        const shipQtyRenderFn = (row: IOrder_ItemUI) => {
            let value = 0;

            if (qtyAssigned[row.guid]) {
                value = qtyAssigned[row.guid].value;
                //     // if (qtyAssigned[row.guid].qty_picked >= 0) {
                //     //     if (qtyAssigned[row.guid].value >= 0) {
                //     //         value = qtyAssigned[row.guid].value;
                //     //     } else {
                //             value = qtyAssigned[row.guid].value;
                //         // }
                //     // } else {
                //     //     value = qtyAssigned[row.guid].value;
                //     // }
            }

            if (packingList.is_locked) {
                return <div>{value}</div>;
            } else {
                return (
                    <>
                        <OwInputWrapper
                            type={"number"}
                            className={"text-end d-print-none"}
                            name={"qty_assigned"}
                            value={value}
                            fn={(e: any) => {
                                handleQtyAssignedChange(e, row);
                            }}
                            disabled={row.Catalog_Item?.has_alternate_sourcing || row.use_lots}
                        />
                        <div className={"d-none d-print-block"}>{value}</div>
                    </>
                );
            }
        };

        const hasLotsRenderFn = (row: IOrder_ItemUI) => {
            if (packingList.is_locked) {
                return null;
            } else {
                return (
                    <OwCheckbox
                        name={"use_lots"}
                        fn={(e: ChangeEvent<any>) => {
                            hasLotsChange(e, row);
                        }}
                        checked={row.Packing_List_Order_Item_Map!.has_lots}
                        truthyVal={true}
                        falsyVal={false}
                        label={"use_lots"}
                    />
                );
            }
        };

        const cartonRenderFn = (row: IOrder_ItemUI) => {
            if (packingList.is_locked) {
                return row.Packing_List_Order_Item_Map?.cartons;
            } else {
                return (
                    <div>
                        <OwInputWrapper
                            name={"cartons"}
                            fn={(e: any) => {
                                handleCartonChange(e, row);
                            }}
                            value={row.Packing_List_Order_Item_Map?.cartons}
                        />
                    </div>
                );
            }
        };

        const calculatedColumns: IOwTableCols[] = [
            {
                title: "order",
                valueProp: "",
                renderFn: orderRenderFn,
                renderFnEmitsRow: true,
                isChecked: () => viewOptions.showOrder,
            },
            {
                title: "item",
                valueProp: "Catalog_Item",
                valueSubProp: "name",
                isChecked: () => viewOptions.showItemName,
            },
            {
                title: "sku",
                valueProp: "Catalog_Item",
                valueSubProp: "sku",
                renderFnEmitsRow: true,
                renderFn: renderSkuFn,
                isChecked: () => viewOptions.showSku,
            },
            {
                title: "upc",
                valueProp: "Catalog_Item",
                valueSubProp: "upc",
                renderFnEmitsRow: true,
                renderFn: renderUpcFn,
                isChecked: () => viewOptions.showUpc,
            },
            {
                title: "uom",
                valueProp: "Catalog_Item",
                valueSubProp: "Unit_Of_Measurement",
                renderFnEmitsRow: true,
                renderFn: renderUomFn,
                isChecked: () => viewOptions.showUom,
            },
            {
                title: "W",
                valueProp: "Catalog_Item",
                valueSubProp: "width",
                className: "text-center",
                isChecked: () => viewOptions.showDimensions,
            },
            {
                title: "H",
                valueProp: "Catalog_Item",
                valueSubProp: "height",
                className: "text-center",
                isChecked: () => viewOptions.showDimensions,
            },
            {
                title: "D",
                valueProp: "Catalog_Item",
                valueSubProp: "depth",
                className: "text-center",
                isChecked: () => viewOptions.showDimensions,
            },
            { title: "description", valueProp: "Catalog_Item", valueSubProp: "description", className: "pre-wrap" },
            {
                title: "pick_qty",
                valueProp: "qty",
                className: "text-end d-print-none",
                renderFn: pickQtyRenderFn,
                renderFnEmitsRow: true,
                isChecked: () => viewOptions.showPickQty,
            },
            {
                title: "order_qty",
                valueProp: "qty",
                className: "text-end",
                renderFn: orderQtyRenderFn,
                renderFnEmitsRow: true,
                isChecked: () => viewOptions.showOrderQty,
            },
            {
                title: "ship_qty",
                valueProp: "ship_qty",
                renderFn: shipQtyRenderFn,
                renderFnEmitsRow: true,
                className: "text-end",
                isChecked: () => viewOptions.showShipQty,
            },
        ];

        if (hasAlternateSources) {
            calculatedColumns.unshift({
                isActionsCol: true,
                title: "",
                valueProp: "",
                hasChevron: true,
                className: "w-3r d-print-none",
                showChevronFn: showChevronFn,
            });
        }

        if (supplierModel?.has_lots) {
            calculatedColumns.push({
                title: "lots",
                valueProp: "",
                renderFn: hasLotsRenderFn,
                renderFnEmitsRow: true,
                className: "text-center d-print-none",
            });
        }

        if (supplierModel?.has_cartons) {
            calculatedColumns.push({
                title: "cartons",
                valueProp: "",
                renderFn: cartonRenderFn,
                renderFnEmitsRow: true,
                className: "text-end w-8r",
            });
        }

        return calculatedColumns;
    }, [viewOptions, packingList, supplierModel, qtyAssigned, handleQtyAssignedChange, hasLotsChange, handleCartonChange]);

    return columns;
};
